import type { IconName, IconSize, IconVariant } from '@lemonade-hq/uniclient-theme';
import type { TagComposerProps, UniclientAction } from '..';
import type { FormattedText, GridResponsiveColumns } from '../utils';
import type { InterpolableTextProps } from './interpolable_text';
import type { UniclientBaseComponentProps, UniclientWizardComponentBase } from './types';

export type CardSize = 'lg-new' | 'lg' | 'md' | 'sm' | 'xl';

export interface SummaryItem {
  readonly label: FormattedText | string;
  readonly value: TagComposerProps | string;
}

export interface TextWithVariant {
  readonly text: string;
  readonly variant: 'bodySmall' | 'h4' | 'h5' | 'h6';
}

export interface Item {
  readonly text: InterpolableTextProps;
  readonly icon?:
    | IconName
    | {
        readonly name: IconName;
        readonly variant: IconVariant;
        readonly size?: IconSize;
      };
  readonly enabled: boolean;
  readonly tooltip?: string;
  readonly additionalInfo?: FormattedText | TagComposerProps;
}

export type SectionHeader =
  | string
  | {
      readonly title: FormattedText;
      readonly additionalInfo: FormattedText;
    };

export interface Section {
  readonly header?: SectionHeader;
  readonly list: Item[];
  readonly withSeparator?: boolean;
}

export type SelectableCardTitle =
  | string
  | {
      readonly text: FormattedText;
      readonly showDiamond?: boolean;
    };

export interface SelectableCard {
  readonly id: string;
  readonly title: SelectableCardTitle;
  readonly subtitle?: TextWithVariant[];
  readonly description?: string;
  readonly items?: Item[] | Section[];
  readonly highlightedText?: string;
  readonly summaryItems?: SummaryItem[];
  readonly customActionText?: string;
  readonly image?: string;
  readonly disabledContent?: boolean;
  readonly additionalAction?: UniclientAction;
}

export interface SelectableCardsProps<TAttribute extends string> extends UniclientBaseComponentProps {
  readonly attribute: TAttribute;
  readonly cards: SelectableCard[];
  readonly action?: UniclientAction;
  readonly multilineTextItem?: boolean;
  readonly selectedCardId?: string;
  readonly selectedActionText?: string;
  readonly selectedIcon?: IconName;
  readonly disableSelectedAction?: boolean;
  readonly cardSize?: CardSize;
  readonly columns?: GridResponsiveColumns;
  readonly clearSelectionText?: string;
  readonly clearSelectionValue?: string;
  /**
   * @optional will center the card into view on mobile viewports
   */
  readonly initiallyFocusedCardId?: string;
}

export type WizardSelectableCardsProps<TAttribute extends string> = SelectableCardsProps<TAttribute> &
  UniclientWizardComponentBase<'selectable_cards'>;

export function selectableCards<TAttribute extends string>(
  props: SelectableCardsProps<TAttribute>,
): WizardSelectableCardsProps<TAttribute> {
  return {
    ...props,
    type: 'selectable_cards',
  };
}
